import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, Input, NgZone, OnDestroy, Output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HelpService } from './help.service';
import { ArticlePart, splitMarkdownIntoParts } from './markdown-parts';
import { HelpArticleDetail } from './models/help-article.model';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

/**
 * Renders a single article: title + body (markdown interleaved with embedded video iframes) plus a
 * sticky table of contents built from the rendered headings (hidden when the container is narrow, e.g.
 * the help drawer, or when there are too few headings). Navigation-agnostic — emits [back].
 */
@Component({
  selector: 'ee-help-article',
  templateUrl: './help-article.component.html',
  styleUrls: ['./help-article.component.scss'],
  standalone: false,
})
export class HelpArticleComponent implements AfterViewInit, OnDestroy {
  @Output() back = new EventEmitter<void>();
  /** Emitted when the requested slug has no visible article, so a host can fall back (e.g. to a collection). */
  @Output() articleNotFound = new EventEmitter<void>();

  /** Embedded in the help drawer: the panel is already a card, so drop the article card framing + back button. */
  @Input() embedded = false;

  @HostBinding('class.hc-embedded')
  get hcEmbedded(): boolean {
    return this.embedded;
  }

  loading = true;
  notFound = false;
  article: HelpArticleDetail | null = null;
  parts: ArticlePart[] = [];

  tocItems: TocItem[] = [];
  activeId: string | null = null;
  showToc = false;

  feedbackState: 'idle' | 'comment' | 'done' = 'idle';
  feedbackComment = '';

  private current = '';
  private resizeObserver?: ResizeObserver;
  private intersectionObserver?: IntersectionObserver;

  @Input()
  set slug(value: string) {
    this.current = value ?? '';
    this.load();
  }

  get slug(): string {
    return this.current;
  }

  constructor(
    private helpService: HelpService,
    private sanitizer: DomSanitizer,
    private host: ElementRef<HTMLElement>,
    private zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.zone.run(() => this.updateShowToc()));
      this.resizeObserver.observe(this.host.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
  }

  /** Called after each <markdown> finishes rendering; (re)builds the TOC from the rendered headings. */
  onMarkdownReady(): void {
    setTimeout(() => this.buildToc(), 0);
  }

  scrollTo(id: string): void {
    this.host.nativeElement.querySelector('#' + CSS.escape(id))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.activeId = id;
  }

  voteHelpful(): void {
    this.sendFeedback(true);
  }

  /** A 'no' reveals an optional comment box first — the comment is the most useful signal for authors. */
  voteNotHelpful(): void {
    this.feedbackState = 'comment';
  }

  submitNotHelpful(): void {
    this.sendFeedback(false, this.feedbackComment);
  }

  private sendFeedback(helpful: boolean, comment?: string): void {
    this.helpService.submitFeedback(this.current, helpful, comment).subscribe({ error: () => undefined });
    this.markVoted(this.current);
    this.feedbackState = 'done';
  }

  private feedbackKey(slug: string): string {
    return `hc-feedback-${slug}`;
  }

  private hasVoted(slug: string): boolean {
    try {
      return !!localStorage.getItem(this.feedbackKey(slug));
    } catch {
      return false;
    }
  }

  private markVoted(slug: string): void {
    try {
      localStorage.setItem(this.feedbackKey(slug), '1');
    } catch {
      // localStorage unavailable (private mode / SSR) — the vote still posts, we just can't gate re-votes.
    }
  }

  private load(): void {
    this.loading = true;
    this.notFound = false;
    this.tocItems = [];
    this.activeId = null;
    this.showToc = false;
    this.intersectionObserver?.disconnect();

    if (!this.current) {
      this.loading = false;
      this.notFound = true;
      return;
    }
    this.helpService
      .get(this.current)
      .pipe(catchError(() => of(null)))
      .subscribe((article) => {
        this.article = article;
        this.notFound = !article;
        this.parts = article ? splitMarkdownIntoParts(article.body_markdown, this.sanitizer) : [];
        this.loading = false;
        if (this.notFound) {
          this.articleNotFound.emit();
        } else {
          this.helpService.recordView(this.current);
          this.feedbackState = this.hasVoted(this.current) ? 'done' : 'idle';
          this.feedbackComment = '';
        }
      });
  }

  private buildToc(): void {
    const headings = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('article h2, article h3')
    );

    const seen = new Map<string, number>();
    const items: TocItem[] = [];
    for (const heading of headings) {
      const text = (heading.textContent || '').trim();
      if (!text) {
        continue;
      }
      let id = this.slugify(text);
      const count = seen.get(id) ?? 0;
      seen.set(id, count + 1);
      if (count > 0) {
        id = `${id}-${count}`;
      }
      heading.id = id;
      items.push({ id, text, level: heading.tagName === 'H3' ? 3 : 2 });
    }

    this.tocItems = items;
    this.setupScrollSpy(headings);
    this.updateShowToc();
  }

  private setupScrollSpy(headings: HTMLElement[]): void {
    this.intersectionObserver?.disconnect();
    if (!headings.length) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries.filter((e) => e.isIntersecting).map((e) => (e.target as HTMLElement).id);
          if (visible.length) {
            this.zone.run(() => (this.activeId = visible[0]));
          }
        },
        { rootMargin: '0px 0px -75% 0px', threshold: 0 }
      );
      headings.forEach((h) => this.intersectionObserver.observe(h));
    });
  }

  private updateShowToc(): void {
    this.showToc = this.host.nativeElement.clientWidth > 760 && this.tocItems.length > 1;
  }

  private slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      || 'section';
  }

}
